import moment from 'moment';
import FleetHelpers, {FLEET_BASE, extractFleetError, fleetFieldErrors, conflictText, fleetFetchAllPages} from './FleetHelpers';

/**
 * Round planner (T021, US2/FR-008..012).
 * POST /api/v1/fleet/trips creates a round (422 ROUND_EXISTS / ROUTE_NOT_ACTIVE,
 * response carries non-blocking `conflicts`). Truck-size recommendation panel
 * reads GET /reports/volume-dashboard (per-route pending volume) and, per
 * existing trip, GET /trips/{id}/volume-recommendation.
 */
export default {
    mixins: [FleetHelpers],
    data: () => ({
        isLoading: false,
        btnLoading: false,
        server_errors: {},

        // Planner filters
        plannerRoutes: [],
        selectedRouteId: '',
        serviceDate: moment(new Date()).format('YYYY-MM-DD'),

        // Existing rounds of the selected route+date
        rounds: [],
        roundsLoading: false,

        // Create-round dialog state
        roundValue: {
            service_date: '',
            round_number: 1,
            truck_id: '',
            driver_ids: [],
            planned_departure_time: '08:00',
            remark: '',
        },
        stopTimetable: [],   // [{sequence, name, planned_arrival_time}]
        conflicts: [],

        // Pickers
        trucks: [],
        drivers: [],
        driversLoading: false,

        // Volume recommendation panel
        volumePanel: null,        // route entry of reports/volume-dashboard
        volumeLoading: false,
        volumeAsOf: '',           // the snapshot date the BACKEND reports (always "now")
        volumeMeasuredAt: '',     // when the figures were measured (backend cache, ISO)
        volumeRefreshing: false,  // stale figures shown while the backend refreshes them
        volumeRefetchTimer: null,
        volumeRequestSeq: 0,      // only the latest FetchVolumePanel call may touch the panel
    }),
    methods: {
        FetchPlannerRoutes() {
            fleetFetchAllPages(this.$http, `${FLEET_BASE}/routes`).then((routes) => {
                this.plannerRoutes = routes;
            }).catch(() => {
            });
        },
        FetchRounds() {
            if (!this.selectedRouteId || !this.serviceDate) {
                this.rounds = [];
                return;
            }
            this.roundsLoading = true;
            this.$http.get(`${FLEET_BASE}/trips`, {
                params: {
                    date: this.serviceDate,
                    route_id: this.selectedRouteId,
                    per_page: 50,
                },
            }).then((res) => {
                this.rounds = res.data.data || [];
                this.roundsLoading = false;
            }).catch(() => {
                this.roundsLoading = false;
            });
        },
        // Truck picker rows: plate + size + capacity. The picker searches
        // client-side, so it needs EVERY active truck, not page 1.
        FetchActiveTrucks() {
            fleetFetchAllPages(this.$http, `${FLEET_BASE}/trucks`, {is_active: 1}).then((trucks) => {
                this.trucks = trucks.map((truck) => ({
                    ...truck,
                    display_name: `${truck.plate_number} — ${this.FleetSizeClassLabel(truck.size_class)}`
                        + ` (${this.FleetFormatNumber(truck.capacity_kg)} kg / ${truck.capacity_cbm} m³)`,
                }));
            }).catch(() => {
            });
        },
        // Multi-driver picker: user_vehicle_departments rows (driver_id =
        // vehicleDepartmentUserId), same source as the driver-user admin page.
        FetchDrivers() {
            this.driversLoading = true;
            this.$axios.get('list-vehicle-department-users', {params: {per_page: 1000}}).then((res) => {
                const rows = (res.data.listVehicleDepartmentUsers && res.data.listVehicleDepartmentUsers.data) || [];
                this.drivers = rows.map((row) => ({
                    id: row.vehicleDepartmentUserId,
                    // The human-searchable driver id on the app = users.user_card_id.
                    cardId: row.userCardid,
                    name: `${row.userName || '...'}${row.userTel ? ' (' + row.userTel + ')' : ''}`,
                    // Picker label — includes the card id so drivers can be found
                    // by the same id the app handover searches by.
                    pickerLabel: `${row.userName || '...'}${row.userTel ? ' (' + row.userTel + ')' : ''}${row.userCardid ? ' · ' + this.$t('fleetRounds.card_id_with_value', {id: row.userCardid}) : ''}`,
                    userName: row.userName,
                    userTel: row.userTel,
                    departmentName: row.vehicle_department_name,
                }));
                this.driversLoading = false;
            }).catch(() => {
                this.driversLoading = false;
            });
        },
        DriverName(driverId) {
            const driver = this.drivers.find((d) => d.id === driverId);
            return driver ? driver.name : `#${driverId}`;
        },
        // The card id (users.user_card_id) the app searches by — resolved from the
        // internal user_vehicle_departments.id. Falls back to the internal id.
        DriverCardId(driverId) {
            const driver = this.drivers.find((d) => d.id === driverId);
            return (driver && driver.cardId) ? driver.cardId : `#${driverId}`;
        },
        // Per-stop timetable rows come from the route detail stops.
        LoadStopTimetable(routeDetail) {
            const rows = [];
            if (routeDetail && routeDetail.start_stop_facility) {
                rows.push({
                    sequence: 1,
                    name: routeDetail.start_stop_facility.name || this.$t('fleetRounds.origin_stop'),
                    planned_arrival_time: '',
                });
            }
            ((routeDetail && routeDetail.stops) || []).forEach((stop) => {
                const duplicatesOrigin = rows.length && rows.length === 1
                    && routeDetail.start_stop_type === stop.stop_type
                    && Number(routeDetail.start_stop_id) === Number(stop.stop_id);
                if (!duplicatesOrigin) {
                    rows.push({
                        sequence: rows.length + 1,
                        name: (stop.facility && stop.facility.name) || `${stop.stop_type} #${stop.stop_id}`,
                        planned_arrival_time: '',
                    });
                }
            });
            this.stopTimetable = rows;
        },
        BuildRoundPayload() {
            // The dialog carries its own date (defaults to the planner filter)
            // so the day a round lands on is always explicit.
            const serviceDate = this.roundValue.service_date || this.serviceDate;
            const departureAt = `${serviceDate} ${this.roundValue.planned_departure_time || '00:00'}:00`;
            const stops = this.stopTimetable
                .filter((row) => row.planned_arrival_time)
                .map((row) => ({
                    sequence: row.sequence,
                    planned_arrival_at: `${serviceDate} ${row.planned_arrival_time}:00`,
                }));
            return {
                route_id: this.selectedRouteId,
                service_date: serviceDate,
                round_number: Number(this.roundValue.round_number),
                truck_id: this.roundValue.truck_id || null,
                driver_ids: this.roundValue.driver_ids || [],
                planned_departure_at: departureAt,
                stops: stops.length ? stops : undefined,
                remark: this.roundValue.remark || undefined,
            };
        },
        CreateRound() {
            this.btnLoading = true;
            this.server_errors = {};
            this.conflicts = [];
            return this.$http.post(`${FLEET_BASE}/trips`, this.BuildRoundPayload()).then((res) => {
                this.btnLoading = false;
                this.conflicts = res.data.conflicts || [];
                this.$notification.OpenNotification_AddItem_OnSuccess('top-right', 'dark', 3000);
                if (this.conflicts.length) {
                    this.$notification.warning({
                        message: this.conflicts.map(conflictText).join(' / '),
                        duration: 6000,
                    });
                }
                return res.data.data;
            }).catch((error) => {
                this.btnLoading = false;
                this.server_errors = fleetFieldErrors(error);
                this.$notification.error({message: extractFleetError(error)});
                return Promise.reject(error);
            });
        },
        // Pending volume + size recommendation for the selected route (US7 feed).
        //
        // Deliberately sends NO date. This endpoint is a LIVE snapshot of what is
        // waiting right now, and it cannot be anything else: latest_enriched_trackings
        // keeps ONE row per parcel, overwritten in place, so the pending set of a past
        // day no longer exists to be queried. We used to send ?date=serviceDate and the
        // backend silently dropped it, which read as "these are the numbers for the day
        // you picked" — they never were. The standalone report page already made this
        // call: see Fleet/Reports/VolumeDashboard.vue ("no date picker — it would be a
        // lie"). We still REFETCH on a date change (freight keeps arriving while the
        // planner is open); we just no longer imply the figure belongs to that date.
        //
        // The backend caches the figures (a cold scan of the main DC's backlog took
        // 8–15 s): stale ones come back at once with is_stale while it refreshes
        // them in the background, so we fetch ONCE more, quietly, a little later.
        //
        // Asks for the selected route ONLY (route_id). Without it the backend
        // computed every active route in the network to show this one panel —
        // ~1 min on prod — and a draft route was not even in that answer.
        FetchVolumePanel(silent = false) {
            if (!this.serviceDate) return;
            clearTimeout(this.volumeRefetchTimer);
            // A newer call (another route, A → B → A, or a cleared route)
            // supersedes this one: its reply and its error are both dropped.
            this.volumeRequestSeq += 1;
            const seq = this.volumeRequestSeq;
            const routeId = this.selectedRouteId;
            if (!routeId) {
                this.volumePanel = null;
                this.volumeLoading = false;
                this.volumeRefreshing = false;
                return;
            }
            if (!silent) {
                this.volumeLoading = true;
                this.volumePanel = null;
            }
            this.$http.get(`${FLEET_BASE}/reports/volume-dashboard`, {params: {route_id: routeId}}).then((res) => {
                if (seq !== this.volumeRequestSeq) return;
                const data = res.data.data || {};
                const routes = data.routes || [];
                this.volumeAsOf = data.date || '';
                this.volumeMeasuredAt = data.as_of || '';
                this.volumePanel = routes.find((r) => Number(r.route_id) === Number(routeId)) || null;
                this.volumeLoading = false;
                this.volumeRefreshing = !silent && !!data.is_stale;
                // A reply landing after the page closed must not schedule a refetch.
                if (this.volumeRefreshing && !this.volumePageClosed) {
                    this.volumeRefetchTimer = setTimeout(() => this.FetchVolumePanel(true), 15000);
                }
            }).catch(() => {
                if (seq !== this.volumeRequestSeq) return;
                this.volumeLoading = false;
                this.volumeRefreshing = false;
            });
        },
    },
    beforeDestroy() {
        this.volumePageClosed = true;
        clearTimeout(this.volumeRefetchTimer);
    },
};
